import {Injectable, inject} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {environment} from '@/environments/environment';
import type {LandingSite, UpdateLandingSiteDto} from '../models';
import {
  fromBackendWebsite,
  toUpdateWebsiteDto,
} from '../adapters/website.adapter';

const API_URL = `${environment.landingBuilderApiUrl}website-builder`;

@Injectable({providedIn: 'root'})
export class LandingSiteService {
  private http = inject(HttpClient);

  /** Get current tenant's website (backend returns array, we take first) */
  getMine(): Observable<LandingSite> {
    return this.http.get<any[]>(`${API_URL}`).pipe(
      map((backends) => {
        // Backend returns array, take first website or create if none
        if (backends && backends.length > 0) {
          return fromBackendWebsite(backends[0]);
        }
        // If no website exists, need to create one first
        throw new Error('No website found. Please create one first.');
      }),
    );
  }

  /** Get specific website by ID */
  getById(id: string): Observable<LandingSite> {
    const numericId = parseInt(id, 10);
    return this.http
      .get<any>(`${API_URL}/${numericId}`)
      .pipe(map((backend) => fromBackendWebsite(backend)));
  }

  update(id: string, dto: UpdateLandingSiteDto): Observable<LandingSite> {
    const backendDto = toUpdateWebsiteDto(dto);
    // Backend uses number IDs, frontend uses strings
    const numericId = parseInt(id, 10);
    return this.http
      .patch<any>(`${API_URL}/${numericId}`, backendDto)
      .pipe(map((backend) => fromBackendWebsite(backend)));
  }

  checkOrganizationName(
    value: string,
    excludeId?: string,
  ): Observable<{available: boolean}> {
    const params: Record<string, string> = {value};
    if (excludeId) params['excludeId'] = excludeId;
    return this.http.get<{available: boolean}>(`${API_URL}/check-subdomain`, {
      params,
    });
  }

  publish(id: string): Observable<LandingSite> {
    const numericId = parseInt(id, 10);
    return this.http
      .post<any>(`${API_URL}/${numericId}/publish`, {})
      .pipe(map((backend) => fromBackendWebsite(backend)));
  }
}
